import { forwardRef } from 'react';

/**
 * Badge Component - A versatile badge for status and labels
 * 
 * @param {Object} props - Component props
 * @param {string} props.variant - Badge variant: 'primary', 'success', 'warning', 'error', 'info', 'gray'
 * @param {string} props.size - Badge size: 'sm', 'md', 'lg'
 * @param {boolean} props.pill - Makes badge fully rounded
 * @param {boolean} props.outline - Makes badge outlined
 * @param {React.ReactNode} props.leftIcon - Icon to display on the left
 * @param {React.ReactNode} props.rightIcon - Icon to display on the right
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Badge content
 */
const Badge = forwardRef(({
  variant = 'gray',
  size = 'md',
  pill = false,
  outline = false,
  leftIcon,
  rightIcon,
  className = '',
  children,
  ...props
}, ref) => {
  // Variant classes
  const variantClasses = {
    primary: outline 
      ? 'border border-primary-600 text-primary-600 bg-transparent'
      : 'bg-primary-100 text-primary-700',
    success: outline
      ? 'border border-success text-success bg-transparent'
      : 'bg-success-light text-success-dark',
    warning: outline
      ? 'border border-warning text-warning-dark bg-transparent'
      : 'bg-warning-light text-warning-dark',
    error: outline
      ? 'border border-error text-error bg-transparent'
      : 'bg-error-light text-error-dark',
    info: outline
      ? 'border border-info text-info bg-transparent'
      : 'bg-info-light text-info-dark',
    gray: outline
      ? 'border border-gray-400 text-gray-600 bg-transparent'
      : 'bg-gray-100 text-gray-700',
    accent: outline
      ? 'border border-accent-500 text-accent-600 bg-transparent'
      : 'bg-accent-100 text-accent-700',
  };

  // Size classes
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-xs',
    lg: 'px-3 py-1 text-sm',
  };

  return (
    <span
      ref={ref}
      className={`
        inline-flex items-center font-medium
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${pill ? 'rounded-full' : 'rounded'}
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      {...props}
    >
      {leftIcon && (
        <span className="mr-1">{leftIcon}</span>
      )}
      {children}
      {rightIcon && (
        <span className="ml-1">{rightIcon}</span>
      )}
    </span>
  );
});

Badge.displayName = 'Badge';

/**
 * StatusBadge Component - Pre-configured badges for common statuses
 */
export const StatusBadge = ({ status, className = '' }) => {
  const statusConfig = {
    // Property statuses
    available: { variant: 'success', label: 'Disponible' },
    rented: { variant: 'info', label: 'Rentado' },
    maintenance: { variant: 'warning', label: 'Mantenimiento' },
    
    // Contract statuses
    active: { variant: 'success', label: 'Activo' },
    expired: { variant: 'error', label: 'Expirado' },
    terminated: { variant: 'gray', label: 'Terminado' },
    pending: { variant: 'warning', label: 'Pendiente' },
    renewed: { variant: 'info', label: 'Renovado' },
    
    // Payment statuses
    paid: { variant: 'success', label: 'Pagado' },
    overdue: { variant: 'error', label: 'Vencido' },
    partial: { variant: 'warning', label: 'Parcial' },
    approved: { variant: 'success', label: 'Aprobado' },
    rejected: { variant: 'error', label: 'Rechazado' },
    
    // General statuses
    completed: { variant: 'success', label: 'Completado' },
    cancelled: { variant: 'gray', label: 'Cancelado' },
    draft: { variant: 'gray', label: 'Borrador' },
    inactive: { variant: 'gray', label: 'Inactivo' },
  };

  const normalizeStatus = (value) => {
    if (typeof value === 'string') return value.toLowerCase();
    if (typeof value === 'number') {
      const enumMap = {
        0: 'draft',
        1: 'active',
        2: 'paid',
        3: 'overdue',
        4: 'pending',
        5: 'partial',
        6: 'approved'
      };
      return enumMap[value] || String(value);
    }
    return '';
  };

  const statusKey = normalizeStatus(status);
  const config = statusConfig[statusKey] || {
    variant: 'gray',
    label: typeof status === 'string' ? status : (status ?? 'Sin estado')
  };

  return (
    <Badge variant={config.variant} className={className}>
      {config.label}
    </Badge>
  );
};

/**
 * DotBadge Component - Badge with a colored dot indicator
 */
export const DotBadge = ({ 
  status = 'neutral', 
  children, 
  className = '' 
}) => {
  const dotColors = {
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error',
    info: 'bg-info',
    neutral: 'bg-gray-400',
  };

  return (
    <span className={`inline-flex items-center text-sm ${className}`}>
      <span className={`w-2 h-2 rounded-full mr-2 ${dotColors[status]}`}></span>
      {children}
    </span>
  );
};

/**
 * CountBadge Component - Badge for showing counts/numbers
 */
export const CountBadge = ({ 
  count, 
  max = 99, 
  variant = 'error',
  className = '' 
}) => {
  const displayCount = count > max ? `${max}+` : count;

  return (
    <Badge 
      variant={variant} 
      size="sm" 
      pill 
      className={`min-w-[20px] justify-center ${className}`}
    >
      {displayCount}
    </Badge>
  );
};

export default Badge;
